import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Container,
  Grid,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationMap from './LocationMap';
import ContactForm from './ContactForm';
import { initFacebookPixel, trackPageView, trackAddToCart, trackLead } from './utils/facebookPixel';
import './App.css';

function App() {
  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessDetails, setBusinessDetails] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [locationRanks, setLocationRanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  // Contact form state
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [autoSubmitTimer, setAutoSubmitTimer] = useState(null);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [ctaClicked, setCTAClicked] = useState(false);
  
  // ID der aktuellen Suche (für die Verknüpfung mit der Server-Queue)
  const [currentSearchId, setCurrentSearchId] = useState(null);
  
  // Timer reference und gespeicherte Ergebnisse für die verzögerte Sendung
  const timerRef = useRef(null);
  const lastSearchResults = useRef(null); // Speichert die letzten Suchergebnisse für den Timer

  // Step definitions
  const steps = [
    'Unternehmen auswählen',
    'Suchbegriff eingeben',
    'Ergebnisse anzeigen'
  ];

  // Handle business search query
  const handleBusinessSearch = async (e) => {
    const value = e.target.value;
    setQuery(value);
    
    if (value.trim() === '') {
      setResults([]);
      return;
    }
    
    try {
      const response = await axios.get(`/api/search?query=${value}`);
      setResults(response.data.predictions || []);
    } catch (err) {
      setError('Error searching for businesses: ' + err.message);
    }
  };

  // Fetch keyword suggestions when a business is selected
  const fetchKeywordSuggestions = async (business) => {
    if (!business || !business.name) return;
    
    setLoadingSuggestions(true);
    setKeywordSuggestions([]);
    
    try {
      const response = await axios.post('/api/keyword-suggestions', {
        businessName: business.name,
        location: business.formatted_address
      });
      
      if (response.data && response.data.suggestions) {
        setKeywordSuggestions(response.data.suggestions);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Keyword-Vorschläge:', err);
      // Wir setzen hier keinen Fehler, damit die Hauptfunktionalität nicht blockiert wird
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Handle selection of a business from results
  const handleSelectBusiness = async (place) => {
    setLoading(true);
    setSelectedBusiness(place);
    setResults([]);
    
    try {
      const response = await axios.get(`/api/details?place_id=${place.place_id}`);
      const businessData = response.data.result;
      setBusinessDetails(businessData);
      setQuery(place.description);
      setActiveStep(1);
      
      // Keywordvorschläge abrufen, nachdem Unternehmensdetails geladen wurden
      fetchKeywordSuggestions(businessData);
    } catch (err) {
      setError('Error loading business details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate concentric pins around the business location
  const generateSearchLocations = (centerLat, centerLng) => {
    const pins = [];
    const radiiKm = [5, 10, 15];
    const pointsPerRing = [6, 14, 18];
    
    const toRad = (deg) => (deg * Math.PI) / 180;
    const toDeg = (rad) => (rad * 180) / Math.PI;
    const earthRadius = 6371; // Earth radius in km

    radiiKm.forEach((radius, ringIndex) => {
      const points = pointsPerRing[ringIndex];
      for (let i = 0; i < points; i++) {
        const bearing = (2 * Math.PI * i) / points;

        const lat1 = toRad(centerLat);
        const lng1 = toRad(centerLng);
        const distance = radius / earthRadius;

        const lat2 = Math.asin(
          Math.sin(lat1) * Math.cos(distance) +
          Math.cos(lat1) * Math.sin(distance) * Math.cos(bearing)
        );
        
        const lng2 =
          lng1 +
          Math.atan2(
            Math.sin(bearing) * Math.sin(distance) * Math.cos(lat1),
            Math.cos(distance) - Math.sin(lat1) * Math.sin(lat2)
          );

        const finalLat = toDeg(lat2);
        const finalLng = toDeg(lng2);

        pins.push({
          lat: finalLat,
          lng: finalLng,
          locationName: `Location ${pins.length + 1}`,
          rank: 20, // Default rank
          companies: [] // Will be filled with companies
        });
      }
    });

    return pins;
  };

  // Perform ranking search with the keyword
  const handleKeywordSearch = async () => {
    if (!keyword.trim() || !businessDetails) {
      setError('Please enter a valid keyword');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Generate search locations
      const locations = generateSearchLocations(
        businessDetails.geometry.location.lat,
        businessDetails.geometry.location.lng
      );
      
      // Fetch rankings for each location
      const locationPromises = locations.map(async (location) => {
        try {
          const response = await axios.get('/api/rank', {
            params: {
              placeId: businessDetails.place_id,
              lat: location.lat,
              lng: location.lng,
              keyword: keyword
            }
          });
          
          return {
            ...location,
            rank: response.data.rank,
            companies: response.data.companies || []
          };
        } catch (error) {
          console.error('Error fetching rank for location:', error);
          return location; // Return original location with default rank
        }
      });
      
      const rankResults = await Promise.all(locationPromises);
      setLocationRanks(rankResults);
      setSearchPerformed(true);
      setActiveStep(2);
      
      // Speichere die Ergebnisse für die verzögerte Sendung
      lastSearchResults.current = rankResults;
      
      // Sende Facebook Pixel AddToCart-Event für die Unternehmenssuche
      try {
        trackAddToCart(businessDetails, keyword);
      } catch (pixelError) {
        console.error('Fehler beim Senden des Facebook Pixel AddToCart-Events:', pixelError);
      }
      
      // Initialer API-Aufruf, um die Suchdaten zur Server-Queue hinzuzufügen
      try {
        const queueResponse = await axios.post('/api/search-complete', {
          placeId: businessDetails.place_id,
          keyword: keyword,
          locationRanks: rankResults,
          isProactive: false
        });
        
        // Speichere die searchId vom Server
        if (queueResponse.data && queueResponse.data.searchId) {
          setCurrentSearchId(queueResponse.data.searchId);
          console.log(`Suche zur Server-Queue hinzugefügt mit ID: ${queueResponse.data.searchId}`);
        }
      } catch (queueError) {
        console.error('Fehler beim Hinzufügen zur Server-Queue:', queueError);
        // Wenn die Server-Queue fehlschlägt, verwenden wir den Client-Timer als Fallback
        startClientSideTimer();
      }
      
      // Keine sofortige Webhook-Sendung mehr - der Server wartet 5 Minuten oder auf Nutzerinteraktion
    } catch (err) {
      setError('Error performing search: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset the form and start over
  const handleReset = () => {
    setActiveStep(0);
    setQuery('');
    setResults([]);
    setSelectedBusiness(null);
    setBusinessDetails(null);
    setKeyword('');
    setLocationRanks([]);
    setSearchPerformed(false);
    setError(null);
    setContactSubmitted(false);
    setCTAClicked(false);
    
    // Zurücksetzen der gespeicherten Daten für die verzögerte Sendung
    lastSearchResults.current = null;
    
    // Clear any existing timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  
  // Handle CTA click with tracking
  const handleCTAClick = async (ctaType = 'checklist') => {
    setCTAClicked(true);
    
    // Track CTA click event
    try {
      if (businessDetails && keyword) {
        await axios.post('/api/track-cta-click', {
          placeId: businessDetails.place_id,
          keyword: keyword,
          ctaType: ctaType,
          averageRank: calculateAverageRank(),
          searchId: currentSearchId
        });
        console.log('CTA Click erfolgreich getrackt');
      }
    } catch (error) {
      console.error('Fehler beim Tracking des CTA Clicks:', error);
    }
    
    // Open contact form
    handleOpenContactForm();
  };

  // Handle opening the contact form
  const handleOpenContactForm = () => {
    setContactFormOpen(true);
    
    // Clear the auto-submit timer when the form is opened
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  
  // Handle contact form submission
  const handleContactFormSubmit = (success) => {
    if (success) {
      setContactSubmitted(true);
      
      // Clear the auto-submit timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  };
  
  // Client-seitiger Timer als Fallback
  const startClientSideTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    console.log('Client-seitiger Fallback-Timer gestartet (5 Minuten)...');
    timerRef.current = setTimeout(() => {
      // Wenn das Kontaktformular nicht bereits ausgefüllt wurde
      if (!contactSubmitted) {
        console.log('Client-seitiger Timer ausgelöst - sende Daten ohne Kontaktinformationen');
        // Verwende die gespeicherten Ergebnisse aus lastSearchResults.current
        submitContactDataAutomatically(lastSearchResults.current);
      } else {
        console.log('Kontaktformular wurde bereits ausgefüllt, keine automatische Absendung notwendig');
      }
    }, 300000); // 5 Minuten (300.000 ms)
  };

  // Submit contact data automatically (when timer expires)
  const submitContactDataAutomatically = async (results) => {
    try {
      if (!businessDetails || !keyword) {
        console.error('Keine businessDetails oder keyword verfügbar für den automatischen Submit');
        return;
      }
      
      if (!results || !Array.isArray(results) || results.length === 0) {
        console.warn('Keine gültigen Ergebnisse für den automatischen Submit verfügbar, verwende locationRanks');
        results = locationRanks;
      }
      
      const avgRank = calculateAverageRank();
      console.log(`Automatische Absendung vorbereitet - placeId: ${businessDetails.place_id}, keyword: ${keyword}, avgRank: ${avgRank}`);
      console.log(`Ergebnisse verfügbar: ${!!results}, Anzahl: ${results?.length || 0}`);
      
      // Detailliertes Logging zur Fehleranalyse
      console.log('Details zum Auto-Submit:');
      console.log(`- businessDetails: ${!!businessDetails} (${typeof businessDetails})`);
      console.log(`- keyword: "${keyword}"`);
      console.log(`- placeId: "${businessDetails?.place_id}"`);
      console.log(`- avgRank: ${avgRank}`);
      
      // Sende vollständige Suchergebnisse nur wenn der Timer abgelaufen ist
      // und der Nutzer nicht proaktiv das Formular ausgefüllt hat
      const payload = {
        placeId: businessDetails.place_id,
        keyword: keyword,
        locationRanks: results, 
        averageRank: avgRank, // Wichtig: Wir übergeben auch den avgRank
        isProactive: false // Explizit als nicht proaktiv markieren
      };
      
      // Wenn eine searchId vorhanden ist, fügen wir sie hinzu (für die Server-Queue)
      if (currentSearchId) {
        payload.searchId = currentSearchId;
      }
      
      console.log('Sende Payload:', JSON.stringify(payload));
      
      const response = await axios.post('/api/search-complete', payload);
      
      console.log('Daten wurden nach Timer-Ablauf automatisch ohne Proaktive Eingabe gesendet', response.data);
      setContactSubmitted(true);
    } catch (error) {
      console.error('Fehler beim automatischen Senden der Daten:', error);
      if (error.response) {
        console.error('Server Antwort bei Fehler:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('Keine Antwort vom Server erhalten');
      } else {
        console.error('Fehler beim Erstellen der Anfrage:', error.message);
      }
    }
  };
  
  // Clean up timer on unmount or when moving between steps
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  
  // Monitor active step changes to clear timer when navigating away from results
  useEffect(() => {
    if (activeStep !== 2 && timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [activeStep]);
  
  // Initialisiere den Facebook Pixel beim ersten Laden
  useEffect(() => {
    initFacebookPixel();
    trackPageView();
  }, []);

  // Average rank calculation
  const calculateAverageRank = () => {
    if (!locationRanks || locationRanks.length === 0) return 0;
    const sum = locationRanks.reduce((acc, loc) => acc + loc.rank, 0);
    return (sum / locationRanks.length).toFixed(1);
  };

  // Best and worst rank
  const getBestRank = () => {
    if (!locationRanks || locationRanks.length === 0) return 0;
    return Math.min(...locationRanks.map(loc => loc.rank));
  };
  
  const getWorstRank = () => {
    if (!locationRanks || locationRanks.length === 0) return 0;
    return Math.max(...locationRanks.map(loc => loc.rank));
  };

  // Get color based on average rank
  const getRankColor = (rank) => {
    if (rank <= 3) return '#10B981'; // Green
    if (rank <= 10) return '#F59E0B'; // Orange
    return '#EF4444'; // Red
  };

  // Render the content based on current step
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ position: 'relative', mt: 3 }}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                borderRadius: 3, 
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)'
              }}
            >
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Wie sichtbar ist dein Handwerksbetrieb in deiner Region?
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom paragraph>
                Gib deinen Unternehmensnamen ein, um zu sehen, wie gut du in den Google Maps-Suchergebnissen vertreten bist:
              </Typography>
              
              <Box sx={{ position: 'relative', mt: 3 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Gib deinen Unternehmensnamen ein"
                  value={query}
                  onChange={handleBusinessSearch}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                  }}
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                />
                
                {results.length > 0 && (
                  <Paper className="search-results" elevation={3}>
                    <List dense>
                      {results.map((result) => (
                        <ListItem 
                          button 
                          key={result.place_id} 
                          onClick={() => handleSelectBusiness(result)}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: 'rgba(0, 0, 0, 0.04)' 
                            },
                            borderBottom: '1px solid #f0f0f0'
                          }}
                        >
                          <ListItemText
                            primary={result.structured_formatting.main_text}
                            secondary={result.structured_formatting.secondary_text}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
              
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                  <CircularProgress />
                </Box>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Paper>
          </Box>
        );
        
      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                borderRadius: 3, 
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)'
              }}
            >
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                Für welchen Suchbegriff möchtest du ranken?
              </Typography>
              
              {businessDetails && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(99, 102, 241, 0.04)', borderRadius: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Ausgewähltes Unternehmen:
                  </Typography>
                  <Typography variant="body1">
                    {businessDetails.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {businessDetails.formatted_address}
                  </Typography>
                </Box>
              )}
              
              <TextField
                fullWidth
                variant="outlined"
                label="Gib einen Suchbegriff ein, nach dem deine Kunden suchen"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                sx={{ 
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
              
              {loadingSuggestions ? (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Keyword-Vorschläge werden geladen...
                  </Typography>
                </Box>
              ) : keywordSuggestions.length > 0 ? (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                    Vorgeschlagene Keywords:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {keywordSuggestions.map((suggestion, index) => (
                      <Chip
                        key={index}
                        label={suggestion}
                        onClick={() => setKeyword(suggestion)}
                        sx={{ 
                          bgcolor: '#6366F120', 
                          color: '#6366F1',
                          '&:hover': {
                            bgcolor: '#6366F130',
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              ) : null}
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Beispiele: "Schreiner", "Badsanierung", "Photovoltaik", "Terrassendach", usw.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleReset}
                  sx={{ borderRadius: 2 }}
                >
                  Zurück
                </Button>
                <Button 
                  variant="contained" 
                  onClick={handleKeywordSearch}
                  disabled={!keyword.trim() || loading}
                  sx={{ 
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #6366F1 0%, #8B5CF6 100%)',
                    boxShadow: '0 4px 10px rgba(99, 102, 241, 0.3)'
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Suchen'}
                </Button>
              </Box>
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Paper>
          </Box>
        );
        
      case 2:
        return (
          <Box sx={{ mt: 3 }}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 4, 
                borderRadius: 3, 
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)'
              }}
            >
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                Deine Google Maps Ranking-Ergebnisse
              </Typography>
              
              {businessDetails && (
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Box sx={{ p: 2, bgcolor: 'rgba(99, 102, 241, 0.04)', borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {businessDetails.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {businessDetails.formatted_address}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip 
                            label={`Suchbegriff: ${keyword}`} 
                            size="small" 
                            sx={{ 
                              bgcolor: '#6366F120', 
                              color: '#6366F1', 
                              fontWeight: 500 
                            }} 
                          />
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box 
                        sx={{ 
                          p: 2, 
                          borderRadius: 2, 
                          bgcolor: 'white',
                          border: '1px solid',
                          borderColor: getRankColor(calculateAverageRank()),
                          boxShadow: `0 4px 10px ${getRankColor(calculateAverageRank())}20`
                        }}
                      >
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Durchschnittliches Ranking
                        </Typography>
                        <Typography 
                          variant="h3" 
                          sx={{ 
                            fontWeight: 700, 
                            color: getRankColor(calculateAverageRank()) 
                          }}
                        >
                          #{calculateAverageRank()}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                          <Chip 
                            label={`Beste: #${getBestRank()}`} 
                            size="small" 
                            sx={{ 
                              bgcolor: '#10B98120', 
                              color: '#10B981', 
                              fontWeight: 500 
                            }} 
                          />
                          <Chip 
                            label={`Schlechteste: #${getWorstRank()}`} 
                            size="small" 
                            sx={{ 
                              bgcolor: '#EF444420', 
                              color: '#EF4444', 
                              fontWeight: 500 
                            }} 
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              <Box className="map-container" sx={{ mb: 4 }}>
                <LocationMap
                  companyLocation={businessDetails?.geometry?.location}
                  companyName={businessDetails?.name}
                  locationRanks={locationRanks}
                />
              </Box>
              
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                  🎯 Wie wirst du die #1 in deiner Region?
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary" sx={{ fontSize: '1.1rem', mb: 3 }}>
                  Erhalte deine <strong>kostenlose, individuelle Checkliste</strong> mit den exakten Schritten, 
                  die dich an die Spitze der Google-Suchergebnisse bringen.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500, mx: 'auto' }}>
                  <Button
                    variant="contained"
                    onClick={() => handleCTAClick('checklist')}
                    sx={{
                      py: 2.5,
                      px: 4,
                      borderRadius: 3,
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 50%, #FFD23F 100%)',
                      boxShadow: '0 8px 25px rgba(255, 107, 53, 0.4)',
                      border: '3px solid #fff',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 35px rgba(255, 107, 53, 0.6)',
                      },
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                        transition: 'left 0.6s',
                      },
                      '&:hover:before': {
                        left: '100%',
                      }
                    }}
                  >
                    🚀 Jetzt kostenlose Checkliste sichern!
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    sx={{
                      borderRadius: 2,
                      borderColor: '#6366F1',
                      color: '#6366F1',
                      '&:hover': {
                        borderColor: '#4F46E5',
                        backgroundColor: 'rgba(99, 102, 241, 0.04)'
                      }
                    }}
                  >
                    Weiteren Suchbegriff prüfen
                  </Button>
                </Box>
              </Box>
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Paper>
          </Box>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="main-container">
      <Box className="header">
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700, 
                background: 'linear-gradient(45deg, #6366F1 0%, #8B5CF6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Google Schnell-Check für Handwerksbetriebe
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src="/img/am-logo-webseite.jpg" 
                alt="AM Logo" 
                style={{ height: '40px', marginLeft: '1rem' }} 
              />
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Box className="step-container">
        <Container maxWidth="lg">
          <Box className="step-indicator">
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          
          {renderStepContent()}
        </Container>
      </Box>
      
      <Box className="footer">
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1 }}>
            © {new Date().getFullYear()} Google Maps Rank Checker - Prüfe dein Unternehmens-Ranking auf Google Maps
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 1 }}>
            <a 
              href="https://www.am-beratung.de/impressum" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ color: '#6366F1', textDecoration: 'none', fontSize: '0.875rem' }}
            >
              Impressum
            </a>
            <a 
              href="https://am-beratung.de/datenschutz" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ color: '#6366F1', textDecoration: 'none', fontSize: '0.875rem' }}
            >
              Datenschutz
            </a>
          </Box>
        </Container>
      </Box>
      
      {/* Contact Form Dialog */}
      <ContactForm
        open={contactFormOpen}
        onClose={() => setContactFormOpen(false)}
        businessDetails={businessDetails}
        keyword={keyword}
        averageRank={calculateAverageRank()}
        searchId={currentSearchId}
        onSubmit={handleContactFormSubmit}
      />
    </div>
  );
}

export default App;